/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * SoonKi Min (skmin@rsupport.com)
 *
 * @description
 *
 * @created at Fri Jun 23 2023
 **/

import { useEffect, useRef } from "react";

export default function useResizeWithDrag<E extends HTMLElement>() {
	const ref = useRef<E>(null);

	const isResizing = useRef(false);
	const startX = useRef(0);
	const startY = useRef(0);
	const startWidth = useRef(0);
	const startHeight = useRef(0);
	const startTop = useRef(0);
	const startLeft = useRef(0);
	const resizingDirection = useRef<{
		right: boolean;
		bottom: boolean;
		left: boolean;
		top: boolean;
	}>({ right: false, bottom: false, left: false, top: false });
	const handleChangeCursor = (event: PointerEvent) => {
		const { clientX, clientY } = event;
		const target = event.currentTarget as HTMLElement;
		const { left, top, right, bottom } = target.getBoundingClientRect();
		const isMouseOnBottom = clientY > bottom - 2 && clientY <= bottom + 2;
		const isMouseOnRight = clientX > right - 2 && clientX <= right + 2;
		const isMouseOnTop = clientY > top - 2 && clientY <= top + 2;
		const isMouseOnLeft = clientX > left - 2 && clientX <= left + 2;
		const isMouseOnRightBottomCorner =
			clientX > right - 10 && clientX <= right + 10 && clientY > bottom - 10 && clientY <= bottom + 10;
		const isMouseOnLeftBottomCorner =
			clientX > left - 10 && clientX <= left + 10 && clientY > bottom - 10 && clientY <= bottom + 10;
		const isMouseOnRightTopCorner =
			clientX > right - 10 && clientX <= right + 10 && clientY > top - 10 && clientY <= top + 10;
		const isMouseOnLeftTopCorner =
			clientX > left - 10 && clientX <= left + 10 && clientY > top - 10 && clientY <= top + 10;
		if (isMouseOnRightBottomCorner) {
			target.style.cursor = "se-resize";
		} else if (isMouseOnLeftBottomCorner) {
			target.style.cursor = "sw-resize";
		} else if (isMouseOnRightTopCorner) {
			target.style.cursor = "ne-resize";
		} else if (isMouseOnLeftTopCorner) {
			target.style.cursor = "nw-resize";
		} else if (isMouseOnBottom) {
			target.style.cursor = "ns-resize";
		} else if (isMouseOnRight) {
			target.style.cursor = "ew-resize";
		} else if (isMouseOnTop) {
			target.style.cursor = "ns-resize";
		} else if (isMouseOnLeft) {
			target.style.cursor = "ew-resize";
		} else {
			target.style.cursor = "default";
		}
	};

	const handleMouseMove = (event: PointerEvent) => {
		if (!isResizing.current || !ref.current) return;
		event.stopPropagation();
		const target = ref.current;

		const viewportWidth = window.innerWidth;
		const viewportHeight = window.innerHeight;

		let newWidth = startWidth.current + (event.clientX - startX.current);
		let newHeight = startHeight.current + (event.clientY - startY.current);

		if (resizingDirection.current.right) {
			const maxWidth = viewportWidth - target.offsetLeft;
			newWidth = Math.min(newWidth, maxWidth);
			target.style.width = `${newWidth}px`;
		}
		if (resizingDirection.current.bottom) {
			const maxHeight = viewportHeight - target.offsetTop;
			newHeight = Math.min(newHeight, maxHeight);
			target.style.height = `${newHeight}px`;
		}
		if (resizingDirection.current.left) {
			const deltaX = startX.current - event.clientX;
			let newLeft = startLeft.current - deltaX;
			let newWidthFromLeft = startWidth.current + deltaX;

			if (newLeft < 0) {
				newWidthFromLeft += newLeft;
				newLeft = 0;
			}

			target.style.width = `${newWidthFromLeft}px`;
			target.style.left = `${newLeft}px`;
		}
		if (resizingDirection.current.top) {
			const HEIGHT_TOP_NAVIGATION_BAR = 60;
			if (event.clientY < HEIGHT_TOP_NAVIGATION_BAR) return;

			const deltaY = startY.current - event.clientY;
			let newTop = startTop.current - deltaY;
			let newHeightFromTop = startHeight.current + deltaY;
			if (newTop < HEIGHT_TOP_NAVIGATION_BAR) {
				newHeightFromTop += newTop - HEIGHT_TOP_NAVIGATION_BAR;
				newTop = HEIGHT_TOP_NAVIGATION_BAR;
			}

			target.style.height = `${newHeightFromTop}px`;
			target.style.top = `${newTop}px`;
		}

		document.documentElement.style.userSelect = "none";
	};

	const handleMouseDown = (event: PointerEvent) => {
		if (!ref.current) return;
		const { clientX, clientY } = event;
		const target = event.currentTarget as HTMLDivElement;
		const { left, top, right, bottom } = target.getBoundingClientRect();
		const isMouseOnBottom = clientY > bottom - 2 && clientY <= bottom + 2;
		const isMouseOnRight = clientX > right - 2 && clientX <= right + 2;
		const isMouseOnTop = clientY > top - 2 && clientY <= top + 2;
		const isMouseOnLeft = clientX > left - 2 && clientX <= left + 2;
		const isMouseOnRightBottomCorner =
			clientX > right - 10 && clientX <= right + 10 && clientY > bottom - 10 && clientY <= bottom + 10;
		const isMouseOnLeftBottomCorner =
			clientX > left - 10 && clientX <= left + 10 && clientY > bottom - 10 && clientY <= bottom + 10;
		const isMouseOnRightTopCorner =
			clientX > right - 10 && clientX <= right + 10 && clientY > top - 10 && clientY <= top + 10;
		const isMouseOnLeftTopCorner =
			clientX > left - 10 && clientX <= left + 10 && clientY > top - 10 && clientY <= top + 10;
		if (
			isMouseOnBottom ||
			isMouseOnRight ||
			isMouseOnTop ||
			isMouseOnLeft ||
			isMouseOnRightBottomCorner ||
			isMouseOnLeftBottomCorner ||
			isMouseOnRightTopCorner ||
			isMouseOnLeftTopCorner
		) {
			event.stopPropagation();
			target.setPointerCapture(event.pointerId);
			isResizing.current = true;
			startX.current = event.clientX;
			startY.current = event.clientY;
			startWidth.current = target.offsetWidth;
			startHeight.current = target.offsetHeight;
			startTop.current = target.offsetTop;
			startLeft.current = target.offsetLeft;
			resizingDirection.current = {
				right: isMouseOnRight,
				bottom: isMouseOnBottom,
				left: isMouseOnLeft,
				top: isMouseOnTop
			};
			if (isMouseOnRightBottomCorner) {
				resizingDirection.current = { right: true, bottom: true, left: false, top: false };
			} else if (isMouseOnLeftBottomCorner) {
				resizingDirection.current = { right: false, bottom: true, left: true, top: false };
			} else if (isMouseOnRightTopCorner) {
				resizingDirection.current = { right: true, bottom: false, left: false, top: true };
			} else if (isMouseOnLeftTopCorner) {
				resizingDirection.current = { right: false, bottom: false, left: true, top: true };
			}
		}
	};
	const stopResizing = (event: PointerEvent) => {
		const target = event.target as HTMLElement;
		isResizing.current = false;
		document.documentElement.style.userSelect = "initial";
		target.releasePointerCapture(event.pointerId);
	};

	useEffect(() => {
		if (!ref.current) return;
		ref.current.onpointerdown = handleMouseDown;
		ref.current.onpointermove = handleChangeCursor;
		ref.current.onpointerup = stopResizing;
		ref.current.onpointerleave = stopResizing;

		return () => {
			if (!ref.current) return;
			ref.current.onpointerdown = null;
			ref.current.onpointermove = null;
			ref.current.onpointerup = null;
			ref.current.onpointerleave = null;
		};
	}, []);

	useEffect(() => {
		document.addEventListener("pointermove", handleMouseMove);
		document.addEventListener("pointerup", stopResizing);
		return () => {
			document.removeEventListener("pointermove", handleMouseMove);
			document.removeEventListener("pointerup", stopResizing);
		};
	}, [ref.current]);

	return { ref };
}
